import { getEncryptedItem, setEncryptedItem } from './encryptedStorage';
import { isBrowser } from './env';
import { uuid as generateUuid } from './uuid';

export type PushPermissionState = 'default' | 'denied' | 'granted';

export interface PushLocalState {
  pushDeviceId: string | null;
  pushRegistered: boolean;
  pushPermission: PushPermissionState;
  pushToken: string | null;
  pushSoundEnabled: boolean;
  pushVibrationEnabled: boolean;
  pushShowPreview: boolean;
  pushLastUpdated: number;
  pushRetryCount: number;
}

const DEFAULT_STATE: PushLocalState = {
  pushDeviceId: null,
  pushRegistered: false,
  pushPermission: 'default',
  pushToken: null,
  pushSoundEnabled: true,
  pushVibrationEnabled: true,
  pushShowPreview: true,
  pushLastUpdated: Date.now(),
  pushRetryCount: 0,
};

function saveState(state: PushLocalState) {
  setEncryptedItem('pushDeviceId', state.pushDeviceId);
  setEncryptedItem('pushRegistered', state.pushRegistered);
  setEncryptedItem('pushPermission', state.pushPermission);
  setEncryptedItem('pushToken', state.pushToken);
  setEncryptedItem('pushSoundEnabled', state.pushSoundEnabled);
  setEncryptedItem('pushVibrationEnabled', state.pushVibrationEnabled);
  setEncryptedItem('pushShowPreview', state.pushShowPreview);
  setEncryptedItem('pushLastUpdated', state.pushLastUpdated);
  setEncryptedItem('pushRetryCount', state.pushRetryCount);
}

export function getPushLocalState(): PushLocalState {
  return {
    pushDeviceId: getEncryptedItem<string>('pushDeviceId'),
    pushRegistered: getEncryptedItem<boolean>('pushRegistered') ?? false,
    pushPermission: (getEncryptedItem<PushPermissionState>('pushPermission') as PushPermissionState) || 'default',
    pushToken: getEncryptedItem<string | null>('pushToken'),
    pushSoundEnabled: getEncryptedItem<boolean>('pushSoundEnabled') ?? true,
    pushVibrationEnabled: getEncryptedItem<boolean>('pushVibrationEnabled') ?? true,
    pushShowPreview: getEncryptedItem<boolean>('pushShowPreview') ?? true,
    pushLastUpdated: getEncryptedItem<number>('pushLastUpdated') ?? Date.now(),
    pushRetryCount: getEncryptedItem<number>('pushRetryCount') ?? 0,
  };
}

export function updatePushLocalState(partial: Partial<PushLocalState>): PushLocalState {
  const current = getPushLocalState();
  const next = { ...current, ...partial, pushLastUpdated: Date.now() };
  saveState(next);
  syncSettingsToServiceWorker(next);
  return next;
}

export function isNotificationSupported() {
  return isBrowser() && 'Notification' in window;
}

export function isServiceWorkerSupported() {
  return isBrowser() && 'serviceWorker' in navigator;
}

export async function requestNotificationPermission(): Promise<PushPermissionState> {
  if (!isNotificationSupported()) return 'denied';
  const result = await Notification.requestPermission();
  updatePushLocalState({ pushPermission: result });
  return result;
}

export function getOrCreateDeviceId(): string {
  const existing = getEncryptedItem<string>('pushDeviceId');
  if (existing) return existing;
  const id = generateUuid('push');
  updatePushLocalState({ pushDeviceId: id });
  return id;
}

export async function initPushSystem() {
  if (!isBrowser()) return;
  const state = getPushLocalState();
  if (!state.pushDeviceId) {
    getOrCreateDeviceId();
  }
  if (isServiceWorkerSupported()) {
    try {
      await navigator.serviceWorker.register('/sw.js');
    } catch {
      // ignore registration errors for now
    }
  }
  syncSettingsToServiceWorker(getPushLocalState());
}

export function enablePushLocally() {
  const permission = getPushLocalState().pushPermission;
  const granted = permission === 'granted';
  updatePushLocalState({ pushRegistered: granted });
}

export function disablePushLocally() {
  updatePushLocalState({ pushRegistered: false });
}

export function setPushSoundEnabled(enabled: boolean) {
  updatePushLocalState({ pushSoundEnabled: enabled });
}

export function setPushVibrationEnabled(enabled: boolean) {
  updatePushLocalState({ pushVibrationEnabled: enabled });
}

export function setPushShowPreview(enabled: boolean) {
  updatePushLocalState({ pushShowPreview: enabled });
}

function syncSettingsToServiceWorker(state: PushLocalState) {
  if (!isBrowser() || !navigator.serviceWorker) return;
  navigator.serviceWorker.ready
    .then((registration) => {
      registration.active?.postMessage({
        type: 'PUSH_SETTINGS_UPDATE',
        payload: {
          sound: state.pushSoundEnabled,
          vibration: state.pushVibrationEnabled,
          showPreview: state.pushShowPreview,
        },
      });
    })
    .catch(() => {});
}

/**
 * Convert VAPID public key from base64 to Uint8Array
 * Required by PushManager.subscribe() API
 * @param base64String - VAPID public key in base64 format
 * @returns Uint8Array representation of the key
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Subscribe to push notifications using VAPID key
 * Returns existing subscription if already subscribed (idempotent)
 * @param vapidPublicKey - VAPID public key for push subscription
 * @returns PushSubscription or null if failed
 */
export async function subscribeToPush(vapidPublicKey: string): Promise<PushSubscription | null> {
  if (!isServiceWorkerSupported()) {
    console.warn('[Push] Service Worker not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.ready;

    // Check if already subscribed (idempotent)
    const existingSubscription = await registration.pushManager.getSubscription();
    if (existingSubscription) {
      return existingSubscription;
    }

    // Subscribe with VAPID key
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });

    return subscription;
  } catch (error) {
    console.error('[Push] Failed to subscribe:', error);
    return null;
  }
}

/**
 * Get current push subscription from service worker
 * @returns PushSubscription or null if not subscribed/unavailable
 */
export async function getPushSubscription(): Promise<PushSubscription | null> {
  if (!isServiceWorkerSupported()) return null;

  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch (error) {
    console.error('[Push] Failed to get subscription:', error);
    return null;
  }
}

/**
 * Extract subscription info for backend registration
 * @param subscription - PushSubscription object
 * @returns Object with endpoint and encryption keys
 */
export function getSubscriptionInfo(subscription: PushSubscription): {
  endpoint: string;
  keys: { p256dh: string; auth: string };
} {
  const keys = subscription.toJSON().keys!;
  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: keys.p256dh!,
      auth: keys.auth!,
    },
  };
}

/**
 * Unsubscribe from push notifications
 * Utility for cleanup/logout scenarios
 * @returns True if successfully unsubscribed
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (!isServiceWorkerSupported()) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await subscription.unsubscribe();
      return true;
    }
    return false;
  } catch (error) {
    console.error('[Push] Failed to unsubscribe:', error);
    return false;
  }
}
